/**
 * Typed surface for Stripe's Crypto Onramp coordinator — the object
 * {@code loadCryptoOnrampAndInitialize(publishableKey, { appearance })} resolves to.
 *
 * Every signature here was read from Stripe's own shipped SDK
 * (js.stripe.com/crypto-onramp/v1/crypto-onramp.js), not inferred:
 *
 *   registerLinkUser(e,t,n,r) { return this.messenger.fns.registerLinkUser(
 *       { email: e, phoneNumber: t, country: n, legalName: r }) }
 *
 *   async authenticate(e,t) { const r = await this.messenger.fns.lookupConsumerSession(
 *       { link_auth_intent_id: e, session_id: this.sessionId });
 *       if (r.requires_authentication) { const i = this.createElement(…) } }
 *
 *   async getMissingIdentifiers() → { identifiers: [{type, regulation}], alternatives: [...] }
 *
 * The coordinator keeps its methods on the prototype, so Object.keys() on the instance shows only
 * state (ready, sessionId, controllerFrame, elementFrames…). The method list came from
 * Object.getOwnPropertyNames(Object.getPrototypeOf(coordinator)) in a real browser run.
 */

export interface OnrampElement {
  mount: (selector: string | HTMLElement) => void;
  destroy: () => void;
}

export interface MissingIdentifier {
  type: string;
  regulation?: string;
}

export interface MissingIdentifiers {
  identifiers: MissingIdentifier[];
  /** Sets of identifiers that can be supplied INSTEAD of the primary list — e.g. a document
   *  where an SSN would otherwise be required. Surfaced but not yet acted on. */
  alternatives: Array<{
    original_missing_identifiers: string[];
    alternative_missing_identifiers: string[];
  }>;
  carf_tin_required?: boolean;
}

/** One identifier the shopper supplies, e.g. { type: 'ssn_last_4', value: '1234' }. The set of
 *  types is decided by the server per region, never hardcoded here — render what it asks for. */
export interface IdentifierValue {
  type: string;
  value: string;
}

/**
 * The base identity profile Stripe wants before it will say what else is missing.
 *
 * US: Stripe requires the FULL 9-digit SSN for the stepped-up check — last-4 is silently
 * rejected, so a shopper entering 4 digits appears to succeed and then stalls.
 * EU: nationalities / birth_city / birth_country are additionally required.
 */
export interface KycProfile {
  given_name: string;
  surname: string;
  address: {
    line1: string;
    city: string;
    postal_code: string;
    country: string;
    state?: string;
  };
  date_of_birth: { year: number; month: number; day: number };
  nationalities?: string[];
  birth_city?: string;
  birth_country?: string;
  id_number?: { value: string; type: string };
}

export interface KycUpdateResult {
  completed: boolean;
  identifiers: MissingIdentifier[];
  alternatives: MissingIdentifiers['alternatives'];
}

/**
 * Only the methods this integration actually drives are declared. The coordinator also exposes
 * deleteWalletAddress, getWalletOwnershipChallenge, submitWalletOwnershipSignature,
 * promptCrsCarfDeclarationInternal and updateKycInfo — left out deliberately rather than typed
 * from guesses, since nothing here calls them yet.
 */
export interface OnrampCoordinator {
  ready?: boolean;
  sessionId?: string;

  /** Creates a Link account. Positional, in this order — see the SDK excerpt above. */
  registerLinkUser: (
    email: string,
    phoneNumber: string,
    country: string,
    legalName: string,
  ) => Promise<unknown>;

  /**
   * Signs a Link user in against the auth intent from 3thix's /auth-intent.
   *
   * Three things about this are easy to get wrong, all read from Stripe's own SDK source:
   *
   * 1. The second argument is a CALLBACK, not options. It fires with
   *    {crypto_customer_id, result} once the shopper finishes, where result is
   *    'success' | 'abandoned' | 'declined'.
   * 2. It resolves with a DETACHED div holding Stripe's OTP iframe. Nothing appears on the page
   *    unless the caller appends that element itself.
   * 3. The promise resolves as soon as the element is mounted — NOT when authentication finishes.
   *    Treating resolution as success leaves the session unauthenticated, which surfaces later as
   *    "User is not authenticated" from getMissingIdentifiers.
   *
   * When Stripe decides no code is needed it skips the element, creates the crypto customer
   * directly, and still invokes the callback — so the callback is the only reliable completion
   * signal for both paths.
   */
  authenticate: (
    linkAuthIntentId: string,
    onComplete: (outcome: {
      crypto_customer_id?: string;
      result: 'success' | 'abandoned' | 'declined';
    }) => void,
  ) => Promise<HTMLElement | null>;

  /** What identity data is still outstanding — drives the SSN vs EU-identifier branch. */
  getMissingIdentifiers: () => Promise<MissingIdentifiers>;

  /**
   * The base identity profile. MUST be submitted before getMissingIdentifiers, which otherwise
   * answers 400 "Basic KYC information must be submitted before this endpoint can be used"
   * (confirmed live 2026-09-10). Shape below matches what 3thix's own client sends.
   */
  submitKycInfo: (info: KycProfile) => Promise<unknown>;

  /**
   * Submits identifier values and reports what is STILL missing — so this is a loop, not a
   * one-shot: keep going until {@code completed} is true. Note it takes an array of
   * {type, value}, not the profile object submitKycInfo takes.
   */
  updateKycInfo: (identifiers: IdentifierValue[]) => Promise<KycUpdateResult>;
  verifyDocuments?: () => Promise<unknown>;
  promptUserAttestation?: () => Promise<unknown>;

  /** Registers the deposit wallet 3thix's /prepare handed back. */
  registerWalletAddress: (address: string, network: string) => Promise<unknown>;

  /**
   * Mounts a Stripe-hosted element. The first argument is a full URL — the SDK ships only
   * .../crypto-onramp/v1/authentication-element.html and .../crs-carf-declaration.html, so this
   * is NOT how card details are collected. See collectPaymentMethod.
   */
  createElement: (url: string, options?: unknown, styles?: unknown) => OnrampElement;

  /**
   * Collects card details. Same shape as authenticate: resolves with a DETACHED form element the
   * caller must mount, and delivers the result through the callback — the promise resolving means
   * "form is ready", not "card added".
   *
   * The wallets option is required by the SDK (3thix's own client notes this); omitting it fails.
   * Card data is entered inside Stripe's iframe and never touches our DOM, which is what keeps
   * this out of PCI scope.
   */
  collectPaymentMethod: (
    options: {
      payment_method_types: string[];
      wallets: { applePay: 'never' | 'auto'; googlePay: 'never' | 'auto' };
    },
    onCollected: (request: {
      cryptoPaymentToken?: string | { id: string };
      payment_token?: string;
      paymentToken?: { id: string };
      id?: string;
    }) => void,
  ) => Promise<HTMLElement | null>;

  /**
   * Drives 3DS and settlement. The callback is handed an onrampSessionId and must return a
   * checkout clientSecret — which comes from 3thix's /sessions/{id}/checkout.
   */
  performCheckout: (
    sessionId: string,
    getClientSecret: (onrampSessionId: string) => Promise<string>,
  ) => Promise<unknown>;

  destroy: () => void;
}

/** Narrowing helper — the SDK is loaded at runtime, so what comes back is unknown until checked. */
export function asCoordinator(value: unknown): OnrampCoordinator {
  if (!value || typeof value !== 'object') {
    throw new Error('Payment SDK did not return a usable session.');
  }
  const candidate = value as Partial<OnrampCoordinator>;
  if (typeof candidate.registerLinkUser !== 'function'
      || typeof candidate.authenticate !== 'function') {
    throw new Error('Payment SDK session is missing expected capabilities.');
  }
  return value as OnrampCoordinator;
}
